import { BaseViewModel } from '../../../../core/services/BaseViewModel';
import { action, makeObservable, observable, runInAction } from 'mobx';
import { Service } from 'typedi';
import { ISpcService } from '../SpcService';
import { EstimateDetailsModel, LeaveSegmentModel, SavedEstimatesModel, SpcLovModel, SpcModel, SpcSteps, SpcStepStatuses, SpcSubmissionModel } from './SpcModels';
import { ServerResponse } from '../../../../core/services/BaseService';
import { formatDate, toLocalDate, toServerDate } from '../../../../core/services/Helper';
import { SpcStepStatus } from './SpcSidebarFormNavigation';
import { Appl } from '../../../../core/Appl';
import { SubmissionStatus } from '../SpcEnums';

@Service() // eslint-disable-next-line
export default class SpcViewModel extends BaseViewModel {
  @observable public memberId?: string;
  @observable public newEstimateStatus = false;
  @observable public unsavedEstimate = true;

  @observable public stepStatus: SpcStepStatuses = {
    serviceType: SpcStepStatus.NotStarted,
    aboutEstimate: SpcStepStatus.NotStarted,
    enterDetails: SpcStepStatus.NotStarted,
    summary: SpcStepStatus.NotStarted,
    estimate: SpcStepStatus.NotStarted,
  };

  @observable public model: SpcModel = {
    submissionId: 0,
    submissionStatusId: SubmissionStatus.Draft,
  };

  @observable public afrFile?: any
  @observable public savedEstimates: SavedEstimatesModel[] = [];
  @observable public spcLovModel: SpcLovModel = {
    serviceTypeListItems: [],
    compensationFrequencyListItems: [],
    employerListItems: [],
    membershipTierListItems: [],
  };

  @observable public currentEstimationDetail: EstimateDetailsModel = {
    spcTypeSubId: 0,
  };

  @observable public isAddingEstimate = false;
  @observable public isEditingEstimate = false;
  @observable public resetEstimate = false;
  @observable public selectedEstimationIndex = 0;

  private _service: ISpcService;

  constructor() {
    super();
    this._service = Appl.Services.get<ISpcService>('ISpcService');
    makeObservable(this);
  }

  @action public async validateReferrerUrl(): Promise<boolean> {
    let referrer = await Appl.Cache.getSessionAsync<string>('Referrer');
    //TODO: Uncomment below aftre DTECK Enables
    if (Appl.Setting.EnvironmentName !== 'local') {
      if (!referrer) {
        this.navigate('/noauthorization');
        return false;
      } else {
        if (!referrer?.toLowerCase().includes(Appl.Setting.MyScersPortalUrl?.toLowerCase()!)) {
          this.navigate('/noauthorization');
          return false;
        }
      }
    }
    return true;
  }

  @action public async validateAndVerifyUrl(url: string): Promise<boolean> {
    let referrer = document.referrer.toLowerCase();
    //TODO: Uncomment below aftre DTECK Enables
    if (Appl.Setting.EnvironmentName !== 'local') {
      let storedReferrer = await Appl.Cache.getSessionAsync<string>('Referrer');
      if (!storedReferrer) {
        if (referrer.includes(Appl.Setting.MyScersPortalUrl?.toLowerCase()!)) {
          await Appl.Cache.addOrUpdateSessionAsync('Referrer', referrer);
        }
      }
      if (!referrer.includes(Appl.Setting.MyScersPortalUrl?.toLowerCase()!)) {
        this.navigate('/noauthorization');
        return false;
      }
    }

    let result = await this._service?.getMemberInfoByUrlAsync({ url });
    if (result.success) {
      runInAction(async () => {
        this.memberId = result.data.memberId;
        if (result.data.isNewUser === true) {
          this.navigate(`/spc/estimate/${this.memberId}/${SpcSteps.ServiceType}`);
        } else {
          if (result.data.isSubmitted === true) {
            this.navigate(`/spc/estimate/saved-estimates/${this.memberId}`);
          } else {
            this.navigate(`/spc/estimate/saved-estimates/${this.memberId}`);
          }
        }
        return true;
      });
    } else {
      Appl.Error.handle(result.error, true);
      return false;
    }
    return false;
  }

  @action
  public async loadStepAsync(step: SpcSteps, memberId: string, passed_spcTypeId?: Number, passed_spcTypeSubId?: Number): Promise<void> {

    console.log("Running load page . . . heaven help us.");
    this.memberId = memberId;
    let result: ServerResponse;
    let refererIsValid = await this.validateReferrerUrl();
    if (!refererIsValid) {
      return;
    }

    const paramsToSearch = new URLSearchParams(window.location.search);
    const url_spcTypeSubId = paramsToSearch.get('spcTypeSubId');
    const url_spcTypeId = paramsToSearch.get('spcTypeId');
    
    let spcTypeId: number | null;
    
    if(url_spcTypeId !== null)
    {
      spcTypeId = Number(url_spcTypeId);
    }
    else
    {
      console.log("Attempting to snag cached value for spcTypeID");
      spcTypeId = await Appl.Cache.getAsync<number>('spcTypeId'); 
    }

    let spcTypeSubId: number | null;
    
    if(url_spcTypeSubId !== null)
    {
      spcTypeSubId = Number(url_spcTypeSubId);
    }
    else
    {
      console.log("Attempting to snag cached value for spcTypeSubID");
      spcTypeSubId = await Appl.Cache.getAsync<number>('spcTypeSubId'); 
    }

    if (this.spcLovModel?.serviceTypeListItems?.length === 0) {
      const lovResponse = await this._service.getSpcLovsAsync();
      if (lovResponse.success) {
        this.spcLovModel = lovResponse.data;
      }
      if (spcTypeSubId && spcTypeSubId > 0) {
        result = await this._service?.getPurchaseCalculatorDetailsByMemberIdAsync(this.memberId!, spcTypeSubId!, spcTypeId!);
        if (result.success) {
          runInAction(async () => {
            this.model = result.data;
            this.setToLocalDates();
            spcTypeId = this.model.spcTypeId!;
          });
        } else {
          let error = Appl.Error.getValidationError(result.error);
          if (error.pointer !== 'no-submission') {
            Appl.Error.handle(result.error, true);
          } else {
            this.navigate('/noauthorization');
          }
          return;
        }
      }
    }

    if (step !== SpcSteps.EnterDetails && step !== SpcSteps.Summary) {
      if (this.isAddingEstimate) {
        this.model.estimateDetails?.splice(this.selectedEstimationIndex, 1);
      }
      this.isEditingEstimate = false;
      this.isAddingEstimate = false;
    }

    if (step === SpcSteps.EnterDetails || step === SpcSteps.Summary) {
      if (step === SpcSteps.Summary) {
        this.currentEstimationDetail = { spcTypeSubId: 0 }
        result = await this._service?.getPurchaseCalculatorDetailsByMemberIdAsync(this.memberId!, spcTypeSubId!, spcTypeId!);
        if (result.success) {
          runInAction(async () => {
            this.model = result.data;
            this.setToLocalDates();
            spcTypeId = this.model.spcTypeId!;
          });
        }
      }
      if(spcTypeId === 1 || spcTypeId === 2 || spcTypeId ===4){
        await this.onEmployerChangeAsync();
      }
    }

    this.model.spcTypeId = spcTypeId!;
    if (step === SpcSteps.Estimate) {
      const result = await this._service.getFinalEstimateAsync(spcTypeSubId!);
      if (result.success) {
        this.model.finalEstimate = result.data;
        this.setToLocalDates();
        await this.setStepStatus(SpcSteps.ServiceType, SpcStepStatus.Completed);
        await this.setStepStatus(SpcSteps.AboutEstimate, SpcStepStatus.Completed);
        await this.setStepStatus(SpcSteps.EnterDetails, SpcStepStatus.Completed);
        await this.setStepStatus(SpcSteps.Summary, SpcStepStatus.Completed);
        await this.setStepStatus(SpcSteps.Estimate, SpcStepStatus.Active);
      } else {
        Appl.Error.handle(result.error, true);
        await this.setStepStatus(SpcSteps.Summary, SpcStepStatus.Active);
        await this.setStepStatus(SpcSteps.Estimate, SpcStepStatus.NotStarted);
        await this.navigateToStepAsync(SpcSteps.Summary);
      }

      const savedEstimateResponse = await this._service.getSavedEstimatesAsync(memberId!);
      if (savedEstimateResponse.success) {
        this.savedEstimates = savedEstimateResponse.data;
      }
    }
    await this.setStepStatus(step, SpcStepStatus.Active);
  }

  public async setStepStatus(step: SpcSteps, status: SpcStepStatus): Promise<void> {
    switch (step) {
      case SpcSteps.ServiceType: {
        this.stepStatus.serviceType = status;
        break;
      }
      case SpcSteps.AboutEstimate: {
        this.stepStatus.aboutEstimate = status;
        break;
      }
      case SpcSteps.EnterDetails: {
        this.stepStatus.enterDetails = status;
        break;
      }
      case SpcSteps.Summary: {
        this.stepStatus.summary = status;
        break;
      }
      case SpcSteps.Estimate: {
        this.stepStatus.estimate = status;
        break;
      }
      default: {
        break;
      }
    }
  }

   private setToLocalDates(): void {
          
     this.model.estimateDetails?.map((estimate) => {
      if(estimate.startDate){
        estimate.startDate = toLocalDate(estimate.startDate);
      }
      if(estimate.endDate){
        estimate.endDate = toLocalDate(estimate.endDate);
      }
      if(estimate.withdrawDate){
        estimate.withdrawDate = toLocalDate(estimate.withdrawDate);
      }
      if(estimate.mbrEntryDate){
        estimate.mbrEntryDate = toLocalDate(estimate.mbrEntryDate); 
      }
      return estimate;
      });
  }

  @action public async getSavedEstimates(memberId?: string): Promise<void> {
    const savedEstimateResponse = await this._service.getSavedEstimatesAsync(memberId!);
    if (savedEstimateResponse.success) {
      this.savedEstimates = savedEstimateResponse.data;
      if (this.savedEstimates.length == 0) {
        await Appl.Cache.addOrUpdateAsync('spcTypeSubId', 0);
        await Appl.Cache.addOrUpdateAsync('spcTypeId', 0);
        if (this.spcLovModel) {
          this.spcLovModel.serviceTypeListItems = [];
        }
        this.navigate(`/spc/estimate/${memberId}/${SpcSteps.ServiceType}`);
      }
    }
  }

  @action public async onServiceTypeStepChangeAsync(): Promise<boolean> {

    let spcTypeId = await Appl.Cache.getAsync<number>('spcTypeId');
    let spcTypeSubId = await Appl.Cache.getAsync<number>('spcTypeSubId');
    if (spcTypeId && spcTypeId > 0) {
      if (spcTypeId !== this.model.spcTypeId) {

        //Conditional prompt if changing saved submission: 
        if(spcTypeSubId && spcTypeSubId > 0 && this.resetEstimate=== false)
        {
           Appl.ConfirmBox.show('Changing the service type will clear any details you have already entered. Do you want to continue?', 'Change Service Type?', '', "Yes, I am sure", "Cancel",
          async () => {
            //Clear cached data related to previous service type
            const result = await this._service.deleteAllEstimationDetailsAsync(spcTypeSubId!);
            this.resetEstimate = true;
            if (result.success) {
              this.model.estimateDetails = [];
              await this.setStepStatus(SpcSteps.ServiceType, SpcStepStatus.Completed);
              await Appl.Cache.addOrUpdateAsync('spcTypeId', this.model.spcTypeId);
              await this.navigateToStepAsync(SpcSteps.AboutEstimate);
            } else {
              this.model.estimateDetails = [];
              await this.setStepStatus(SpcSteps.ServiceType, SpcStepStatus.Completed);
              await Appl.Cache.addOrUpdateAsync('spcTypeId', this.model.spcTypeId);
              await this.navigateToStepAsync(SpcSteps.AboutEstimate);
            }
          }, () => { return false }, true);
        }
        //Conditional prompt if only changing type for an unsaved submission:
        else
        {
          Appl.ConfirmBox.show('You are changing the existing service type. Do you want to continue?', 'Change Service Type?', '', "Yes, I am sure", "Cancel",
          async () => {
            //Clear cached data related to previous service type
            const result = await this._service.deleteAllEstimationDetailsAsync(spcTypeSubId!);
            if (result.success) {
              this.model.estimateDetails = [];
              await this.setStepStatus(SpcSteps.ServiceType, SpcStepStatus.Completed);
              await Appl.Cache.addOrUpdateAsync('spcTypeId', this.model.spcTypeId);
              await this.navigateToStepAsync(SpcSteps.AboutEstimate);
            } else {
              this.model.estimateDetails = [];
              await this.setStepStatus(SpcSteps.ServiceType, SpcStepStatus.Completed);
              await Appl.Cache.addOrUpdateAsync('spcTypeId', this.model.spcTypeId);
              await this.navigateToStepAsync(SpcSteps.AboutEstimate);
            }
          }, () => { return false }, true);
        }
       
      } else {
        await this.setStepStatus(SpcSteps.ServiceType, SpcStepStatus.Completed);
        await Appl.Cache.addOrUpdateAsync('spcTypeId', this.model.spcTypeId);
        await this.navigateToStepAsync(SpcSteps.AboutEstimate);
      }
    } else {
      await this.setStepStatus(SpcSteps.ServiceType, SpcStepStatus.Completed);
      await Appl.Cache.addOrUpdateAsync('spcTypeId', this.model.spcTypeId);
      await this.navigateToStepAsync(SpcSteps.AboutEstimate);
    }
    return true;
  }

  @action public async onAboutEstimateStepChangeAsync(): Promise<boolean> {
    
    await this.setStepStatus(SpcSteps.AboutEstimate, SpcStepStatus.Completed);
    if (this.model.estimateDetails && this.model.estimateDetails.length > 0) {
      await this.setStepStatus(SpcSteps.EnterDetails, SpcStepStatus.Completed);
      await this.navigateToStepAsync(SpcSteps.Summary);
    } else {
      await this.navigateToStepAsync(SpcSteps.EnterDetails);
    }
    return true;
  }

  @action public async onEmployerChangeAsync(): Promise<void> {
    this.spcLovModel.membershipTierListItems = [];
    const result = await this._service.getMembershipTierByEmployerAsync(this.currentEstimationDetail.mbrEmployer!);
    if (result.success) {
      this.spcLovModel.membershipTierListItems = result.data;
    } else {
      Appl.Error.handle(result.error, true);
    }
  }

  @action
  public async onEnterDetailsStepChangeAsync(): Promise<boolean> {
    
    const paramsToSearch = new URLSearchParams(window.location.search);
    const url_spcTypeId = paramsToSearch.get('spcTypeId');
    const url_spcTypeSubId = paramsToSearch.get('spcTypeSubId');

    this.currentEstimationDetail.spcTypeId = await Appl.Cache.getAsync<any>('spcTypeId');
    if (!this.currentEstimationDetail.spcTypeId) {
      this.currentEstimationDetail.spcTypeId = Number(url_spcTypeId);
    }
    this.currentEstimationDetail.spcTypeSubId = await Appl.Cache.getAsync<any>('spcTypeSubId');
    if (!this.currentEstimationDetail.spcTypeSubId) {
      this.currentEstimationDetail.spcTypeSubId = Number(url_spcTypeSubId);
    }

    this.currentEstimationDetail.memberId = this.memberId;
    this.newEstimateStatus = false;
    this.unsavedEstimate = false;
    let isValid = await this.validateEstimationFormAsync();
    if (!isValid) {
      return false;
    }
    if (this.currentEstimationDetail.startDate) {
      this.currentEstimationDetail.startDate = toServerDate(this.currentEstimationDetail.startDate);
    }
    if (this.currentEstimationDetail.endDate) {
      this.currentEstimationDetail.endDate = toServerDate(this.currentEstimationDetail.endDate);
    }

    if (this.currentEstimationDetail.spcTypeId === 2 || this.currentEstimationDetail.spcTypeId === 4) {
      if (this.currentEstimationDetail.mbrEntryDate) {
        this.currentEstimationDetail.mbrEntryDate = toServerDate(this.currentEstimationDetail.mbrEntryDate);
      }
    }
    if (this.currentEstimationDetail.spcTypeId === 3) {
      if (this.currentEstimationDetail.withdrawDate) {
        this.currentEstimationDetail.withdrawDate = toServerDate(this.currentEstimationDetail.withdrawDate);
      }
    }

    const result = await this._service.createOrUpdateEstimateDetailsAsync(this.currentEstimationDetail);
    if (result.success) {
      this.currentEstimationDetail.spcReqId = result.data.spcReqId;
      this.currentEstimationDetail.spcTypeSubId = result.data.spcTypeSubId;
      await Appl.Cache.addOrUpdateAsync('spcTypeSubId', result.data.spcTypeSubId);
      if (!this.model.estimateDetails) {
        this.model.estimateDetails = [];
      }
      if (this.model.estimateDetails && this.model.estimateDetails.length === 0) {
        this.model.estimateDetails.push(this.currentEstimationDetail);
      }
      await this.setStepStatus(SpcSteps.EnterDetails, SpcStepStatus.Completed);
      const searchParams = new URLSearchParams();
      searchParams.set('spcTypeId', String(this.currentEstimationDetail.spcTypeId));
      searchParams.set('spcTypeSubId', String(this.currentEstimationDetail.spcTypeSubId));
      await this.navigateToStepAsync(
        `${SpcSteps.Summary}?${searchParams.toString()}`
      );
      this.resetEstimate = false;
      return true;
    } else {
      Appl.Error.handle(result.error, true);
      return false;
    }
  }

  @action
  public async onSummaryStepChangeAsync(): Promise<boolean> {
    const paramsToSearch = new URLSearchParams(window.location.search);
    const url_spcTypeId = paramsToSearch.get('spcTypeId');
    const url_spcTypeSubId = paramsToSearch.get('spcTypeSubId');
    
    //Assigning spcTypeId from URL if it's not in the cache:
    this.currentEstimationDetail.spcTypeId = await Appl.Cache.getAsync<any>('spcTypeId');
    if (!this.currentEstimationDetail.spcTypeId) {
      this.currentEstimationDetail.spcTypeId = Number(url_spcTypeId);
    }

    //Assigning spcTypeSubId from URL if it's not in the cache:
    this.currentEstimationDetail.spcTypeSubId = await Appl.Cache.getAsync<any>('spcTypeSubId');
    if (!this.currentEstimationDetail.spcTypeSubId) {
      this.currentEstimationDetail.spcTypeSubId = Number(url_spcTypeSubId);
    }
    const result = await this._service.acceptEstimationTermsAsync(this.currentEstimationDetail.spcTypeSubId, this.currentEstimationDetail.spcTypeId!);
    if (result.success) {
      await this.setStepStatus(SpcSteps.Summary, SpcStepStatus.Completed);
      this.unsavedEstimate = false;
      const searchParams = new URLSearchParams();
      searchParams.set('spcTypeId', String(url_spcTypeId));
      searchParams.set('spcTypeSubId', String(url_spcTypeSubId));
      await this.navigateToStepAsync(
        `${SpcSteps.Estimate}?${searchParams.toString()}`
      );
      return true;
    } else {
      Appl.Error.handle(result.error, true);
      return false;
    }
  }

  @action public async addNewEstimationAsync(): Promise<void> {
    this.isAddingEstimate = true;
    this.isEditingEstimate = false;
    let spcType = await Appl.Cache.getAsync<any>('spcTypeId');
    this.selectedEstimationIndex = 0;
    this.currentEstimationDetail = { spcTypeSubId: 0, spcTypeId: spcType };
    if (this.model.estimateDetails) {
      this.model.estimateDetails.push(this.currentEstimationDetail);
      this.selectedEstimationIndex = this.model.estimateDetails?.length - 1;
    }
    await this.navigateToStepAsync(SpcSteps.EnterDetails);
  }

  @action public async editEstimationAsync(index: number, url?: string): Promise<void> {
    this.isEditingEstimate = true;
    this.isAddingEstimate = false;3
    this.selectedEstimationIndex = index;
    if (this.model.estimateDetails) {
      this.currentEstimationDetail = this.model.estimateDetails[index] as EstimateDetailsModel;
    }
    await this.navigateToStepAsync(`${SpcSteps.EnterDetails}?${url}`);
  }

  @action public async cancelAddEstimateAsync(): Promise<void> {
    if (this.isAddingEstimate) {
      this.model.estimateDetails?.splice(this.selectedEstimationIndex, 1);
    }
    this.isEditingEstimate = false;
    this.isAddingEstimate = false;
    this.currentEstimationDetail = { spcTypeSubId: 0 };
    await this.setStepStatus(SpcSteps.EnterDetails, SpcStepStatus.Completed);
    await this.navigateToStepAsync(SpcSteps.Summary);
  }

  @action public async removeEstimationAsync(index: number): Promise<void> {
    if (this.model.estimateDetails && this.model.estimateDetails.length > 0) {
      let estimate = this.model.estimateDetails[index];
      let spcType = await Appl.Cache.getAsync<any>('spcTypeId');
      const deleteResponse = await this._service?.deleteEstimationDetailAsync(estimate.spcReqId!, spcType);
      if (deleteResponse.success) {
        runInAction(() => {
          this.model.estimateDetails?.splice(index, 1);
          if (this.model.estimateDetails?.length === 0) {
            this.navigateToStepAsync(SpcSteps.EnterDetails);
          }
          this.currentEstimationDetail = { spcTypeSubId: 0 };
        });
      } else {
        Appl.Error.handle(deleteResponse.error, true);
      }
    }
  }

  @action public async validateEstimationFormAsync(): Promise<boolean> {
    Appl.Validator.init();
    if (
      this.currentEstimationDetail.spcTypeId === 1 ||
      this.currentEstimationDetail.spcTypeId === 2 ||
      this.currentEstimationDetail.spcTypeId === 4
    ) {
      await Appl.Validator.validateString('Employer', `mbrEmployer`, this.currentEstimationDetail.mbrEmployer, true);
      if (this.currentEstimationDetail.spcTypeId === 2 || this.currentEstimationDetail.spcTypeId === 4) {
        await Appl.Validator.validateDate('Membership Entry Date', `mbrEntryDate`, this.currentEstimationDetail.mbrEntryDate, true, 'MMDDYYYY');
      }
      await Appl.Validator.validateNumber('Membership Tier', `mbrTierId`, this.currentEstimationDetail.mbrTierId, true, 100, 1);
      await Appl.Validator.validateDate('From Date', `startDate`, this.currentEstimationDetail.startDate, true, 'MMDDYYYY');
      await Appl.Validator.validateDate('To Date', `endDate`, this.currentEstimationDetail.endDate, true, 'MMDDYYYY');
      await Appl.Validator.validateNumber('Estimated Hours of Leave', `estSvcHours`, this.currentEstimationDetail.estSvcHours, true);
      await Appl.Validator.validateNumber('Pay Rate', `estmComp`, this.currentEstimationDetail.estmComp, true);
      await Appl.Validator.validateNumber('Pay Frequency', `comFreqId`, this.currentEstimationDetail.comFreqId, true);
    }
    if (this.currentEstimationDetail.spcTypeId === 3) {
      await Appl.Validator.validateDate('Withdrawal Date', `withdrawDate`, this.currentEstimationDetail.withdrawDate, true, 'MMDDYYYY');
      await Appl.Validator.validateNumber('Amount Withdrawn', `withdrawAmount`, this.currentEstimationDetail.withdrawAmount, true);
    }
    return Appl.Validator.isValid();
  }

  @action public async onEstimationDateChangeAsync(field_id?: number): Promise<void> {

      let temp_id = await Appl.Cache.getAsync<any>('spcTypeId');

      if (this.currentEstimationDetail.startDate && this.currentEstimationDetail.endDate) 
      {
        let fromString = toServerDate(this.currentEstimationDetail.startDate)?.split('T')[0];
        let toString = toServerDate(this.currentEstimationDetail.endDate)?.split('T')[0];
        let mbrEntryDateString = undefined;
        if (this.currentEstimationDetail.mbrEntryDate) {
          mbrEntryDateString = toServerDate(this.currentEstimationDetail.mbrEntryDate)?.split('T')[0];
        }
        let spcType = await Appl.Cache.getAsync<any>('spcTypeId');
        const result = await this._service?.getLeaveHours({ fromDate: fromString, toDate: toString!, spcTypeId: spcType, mbrEntryDate: mbrEntryDateString });
        if (result.success) {
          let leaveSegment = result?.data as LeaveSegmentModel;
          if (leaveSegment) {
            this.currentEstimationDetail.estSvcHours = leaveSegment.segment1EstSvcHours;
            this.currentEstimationDetail.fiscalYearLabel1 = `${leaveSegment.segment1FiscalYear} (${formatDate(leaveSegment.segment1StartDate)} - ${formatDate(leaveSegment.segment1EndDate)})`
            this.currentEstimationDetail.estSvcSplitHours = leaveSegment.segment2EstSvcHours;
            this.currentEstimationDetail.fiscalYearLabel2 = `${leaveSegment.segment2FiscalYear} (${formatDate(leaveSegment.segment2StartDate)} - ${formatDate(leaveSegment.segment2EndDate)})`
            if(leaveSegment.segment2EstSvcHours! >= 0)
            {
              this.currentEstimationDetail.splitSegment = true;
            }
            else
            {
              this.currentEstimationDetail.splitSegment = false;
            }
          }
        } 
        else 
        {
          Appl.Error.handle(result.error, true);
        }
      }
  }

  @action public async onDeleteSavedEstimateAsync(spcTypeSubId: number): Promise<void> {
    const result = await this._service.deleteSavedEstimatesAsync(spcTypeSubId);
    if (result.success) {
      this.savedEstimates = this.savedEstimates.filter((o) => o.spcTypeSubId !== spcTypeSubId);
    } else {
      Appl.Error.handle(result.error, true);
    }
  }

  @action public async resetSavedEstimateAsync(memberId: string, redirect: boolean=false): Promise<void> {
    const temp_subid = await Appl.Cache.getAsync<any>('spcTypeSubId');
    const result = await this._service.deleteSavedEstimatesAsync(temp_subid);
    if (result.success) {
      this.savedEstimates = this.savedEstimates.filter((o) => o.spcTypeSubId !== temp_subid);
      this.beginNewEstimate(memberId!,true);
    } else {
      Appl.Error.handle(result.error, true);
    }
    this.unsavedEstimate = true;
  }

  @action public async navigateToStepAsync(url: string): Promise<boolean> {

    this.navigate(`/spc/estimate/${this.memberId}/${url}`);
    return true;
  }

  @action public async onPrintEstimateAsync(): Promise<void> {
    const spcTypeSubId = await Appl.Cache.getAsync<any>('spcTypeSubId');
    this.navigate(`/spc/estimate/${this.memberId}/${spcTypeSubId}/print`)
  }

  @action public async onPrintEstimateAsync2(): Promise<void> 
  {
    const spcTypeSubId = await Appl.Cache.getAsync<number>('spcTypeSubId');

    const pdfBlob = await this.downloadEstimate2(spcTypeSubId as unknown as number);

    const pdfUrl = URL.createObjectURL(pdfBlob) + '#view=fit';

    window.open(pdfUrl, '_blank');
  }

  @action
  public async beginNewEstimate(memberId: string, redirect: boolean=false): Promise<void> {
    await Appl.Cache.addOrUpdateAsync('spcTypeSubId', 0);
    await Appl.Cache.addOrUpdateAsync('spcTypeId', 0);
    this.spcLovModel.serviceTypeListItems = [];
    this.model = {
      submissionId: 0,
      submissionStatusId: SubmissionStatus.Draft,
      estimateDetails: []
    };
    this.newEstimateStatus = true;
    this.unsavedEstimate = true;
    this.currentEstimationDetail = {
      spcTypeSubId: 0
    };
     this.stepStatus = {
      serviceType: SpcStepStatus.NotStarted,
      aboutEstimate: SpcStepStatus.NotStarted,
      enterDetails: SpcStepStatus.NotStarted,
      summary: SpcStepStatus.NotStarted,
      estimate: SpcStepStatus.NotStarted,
    };
    this.selectedEstimationIndex = 0;
    this.isAddingEstimate = false;
    this.isEditingEstimate = false;
    this.selectedEstimationIndex = 0;
    this.navigate(`/spc/estimate/${memberId}/${SpcSteps.ServiceType}`);
  }

  @action
  public async editSavedEstimateAsync(memberId: string, spcTypeSubId: number, spcTypeId: number): Promise<void> {
    const searchParams = new URLSearchParams();
    searchParams.set('spcTypeId', String(spcTypeId));
    searchParams.set('spcTypeSubId', String(spcTypeSubId));
    await Appl.Cache.addOrUpdateAsync('spcTypeSubId', spcTypeSubId);
    await Appl.Cache.addOrUpdateAsync('spcTypeId', spcTypeId);
    this.spcLovModel.serviceTypeListItems = [];
    await this.setStepStatus(SpcSteps.ServiceType, SpcStepStatus.Completed);
    await this.setStepStatus(SpcSteps.AboutEstimate, SpcStepStatus.Completed);
    await this.setStepStatus(SpcSteps.EnterDetails, SpcStepStatus.Completed);

    this.currentEstimationDetail = { spcTypeSubId: 0 }
    let temp_result = await this._service?.getPurchaseCalculatorDetailsByMemberIdAsync(this.memberId!, spcTypeSubId!, spcTypeId!);
    if (temp_result.success) {
      runInAction(async () => {
        this.model = temp_result.data;
        this.setToLocalDates();
        spcTypeId = this.model.spcTypeId!;
      });
    }

    //Directing user to the summary page if this is an incomplete estimate: 
    if(this.model.spcAck != 'Y')
    {
      await this.setStepStatus(SpcSteps.Summary, SpcStepStatus.Active);
      await this.setStepStatus(SpcSteps.Estimate, SpcStepStatus.NotStarted);
      var decodedUrl = decodeURIComponent(location.href);
      if (decodedUrl.includes(`${memberId}/${SpcSteps.Summary}`)) {
        location.href = `/spc/estimate/${memberId}/${SpcSteps.Summary}?${searchParams.toString()}`;
      } else {
        this.navigate(`/spc/estimate/${memberId}/${SpcSteps.Summary}?${searchParams.toString()}`);
      }
      this.unsavedEstimate = true;
    }
    else
    {
      //Directing user to the final estimate page if this is a complete estimate: 
      await this.setStepStatus(SpcSteps.Summary, SpcStepStatus.Completed);
      await this.setStepStatus(SpcSteps.Estimate, SpcStepStatus.Active);
      var decodedUrl = decodeURIComponent(location.href);
      if (decodedUrl.includes(`${memberId}/${SpcSteps.Estimate}`)) {
        location.href = `/spc/estimate/${memberId}/${SpcSteps.Estimate}?${searchParams.toString()}`;
      } else {
        this.navigate(`/spc/estimate/${memberId}/${SpcSteps.Estimate}?${searchParams.toString()}`);
      }
      this.unsavedEstimate = false;
    }
  }

  @action public async downloadEstimate(spcTypeSubId: number): Promise<void> 
  {
    const result = await this._service?.downloadAsync(
      `/spc/purchase-calculator/estimate-as-pdf/${spcTypeSubId}`
    );

    if (result.success) 
    {
      this.afrFile = result.data;
    } 
    else 
    {
      Appl.Error.handle(result.error, true);
    }
  }


  @action public async downloadEstimate2(spcTypeSubId: number): Promise<Blob>
  {
    const result = await this._service?.downloadAsync(
      `/spc/purchase-calculator/estimate-as-pdf/${spcTypeSubId}`
    );

    if (result.success) 
    {
      const pdfBlob = new Blob(
        [result.data],
        { type: 'application/pdf' }
      );

      this.afrFile = result.data;

      return pdfBlob;
    } 
    else 
    {
      Appl.Error.handle(result.error, true);
      throw new Error('Failed to download estimate PDF.');
    }
  }

  //Code to set completed steps if page is refreshed:
  @action public setCompletedSteps(stepSlug: string): void
  {
    switch (stepSlug) {
      case SpcSteps.ServiceType: {
        break;
      }
      case SpcSteps.AboutEstimate: {
        this.stepStatus.serviceType = SpcStepStatus.Completed;
        break;
      }
      case SpcSteps.EnterDetails: {
        this.stepStatus.serviceType = SpcStepStatus.Completed;
        this.stepStatus.aboutEstimate = SpcStepStatus.Completed;
        break;
      }
      case SpcSteps.Summary: {
        this.stepStatus.serviceType = SpcStepStatus.Completed;
        this.stepStatus.aboutEstimate = SpcStepStatus.Completed;
        this.stepStatus.enterDetails = SpcStepStatus.Completed;
        break;
      }
      case SpcSteps.Estimate: {
        this.stepStatus.serviceType = SpcStepStatus.Completed;
        this.stepStatus.aboutEstimate = SpcStepStatus.Completed;
        this.stepStatus.enterDetails = SpcStepStatus.Completed;
        this.stepStatus.summary = SpcStepStatus.Completed;
        break;
      }
    }
  }

}
